<template>
  <ProvideApiFilebank v-slot="{ deleteFiles }">
    <WdsModal
      :show="isOpen"
      :title="modalTitle"
      data-test="expense-modal"
      @cancel="onCancel(deleteFiles)"
    >
      <div>
        <label class="m-t-10 f-500">{{ $t('Vendor') }} *</label>
        <input
          v-model="expenseState.vendor"
          :placeholder="$t('Enter vendor name')"
          name="vendor"
          type="text"
          class="form-control"
          data-test="expense-modal-vendor"
          :disabled="viewOnly"
        />
        <label class="m-t-20 f-500">{{ $t('Description') }}</label>
        <input
          v-model="expenseState.description"
          :placeholder="$t('Enter description')"
          name="description"
          type="text"
          class="form-control"
          data-test="expense-modal-description"
          :disabled="viewOnly"
        />
        <div class="row m-t-20">
          <div class="col-sm-6">
            <label class="f-500">{{ $t('Expense date') }} *</label>
            <PickerDatetime
              v-model="expenseState.expenseAt"
              :inline="true"
              :as-input="true"
              :format="$options.dateFormat"
              :disabled="viewOnly"
            />
          </div>
          <div class="col-sm-6">
            <label class="f-500">
              {{ $t('Amount') }}
              {{ $t(shouldExcludeVat ? `Excl. ${vatTerminology}` : `Incl. ${vatTerminology}`) }} *
            </label>
            <CurrencyConversionInput
              v-model="amount"
              :disabled="viewOnly"
              :desired-currency-code="expenseReportCurrency"
              :user-currency-code="account.currency"
              :helper-input-label="`* ${account.currency}`"
            />
          </div>
        </div>
        <div v-if="shouldExcludeVat && !viewOnly" class="alert alert-info" role="alert">
          {{ $t(`Please make sure to enter the amount excluding ${vatTerminology}`) }}
        </div>
        <div class="m-b-10">
          <label class="f-500">
            {{ $t('Files') }}
            <span v-if="filesRequired">*</span>
          </label>
          <br />
          <BaseFileAttachmentFromOs
            v-model="selectedFiles"
            :disabled="viewOnly"
            :default-tags="['Expenses']"
            @update-busy-status="handleUpdatedBusyStatus"
          />
        </div>
      </div>
      <template #footer>
        <div class="tw-flex tw-justify-between">
          <WdsButton default @click="onCancel(deleteFiles)">
            {{ viewOnly ? $t('Close') : $t('Cancel') }}
          </WdsButton>
          <WdsButton
            v-if="!viewOnly"
            primary
            data-test="confirm-expense-button"
            :disabled="saveState === 'saving' || saveState === 'saved' || isDisabled"
            @click="saveExpense"
          >
            {{ saveButtonText }}
          </WdsButton>
        </div>
      </template>
    </WdsModal>
  </ProvideApiFilebank>
</template>

<script>
import gql from 'graphql-tag'
import moment from 'moment'
import { WdsButton, WdsModal } from '@worksome/wds-components'
import BaseFileAttachmentFromOs from '@/components/BaseFileAttachmentFromOs.vue'
import CurrencyConversionInput from '@/components/CurrencyConversionInput.vue'
import PickerDatetime from '@/components/PickerDatetime.vue'
import ProvideApiFilebank from '@/components/ProvideApiFilebank.vue'
import { EXPENSE_REPORT_MUTATIONS } from '@/state/mutation-types'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'

export default {
  components: {
    WdsButton,
    WdsModal,
    BaseFileAttachmentFromOs,
    CurrencyConversionInput,
    PickerDatetime,
    ProvideApiFilebank,
  },
  dateFormat: 'YYYY-MM-DD',
  props: {
    expenseReportId: { type: Number, default: 0 },
    expenseReportCurrency: { type: String, required: true },
    expense: { type: Object, default: () => ({}) },
    isOpen: { type: Boolean, default: false },
    filesRequired: { type: Boolean, default: false },
    // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
    shouldExcludeVat: { type: Boolean, default: true },
    viewOnly: { type: Boolean, default: false },
  },
  emits: ['closed', 'expense-created', 'expense-updated'],
  data() {
    return {
      saveState: 'fresh',
      expenseState: this.getInitialExpenseState(),
      amount: this.getInitialAmount(),
      selectedFiles: [],
      fileComponentIsWorking: false,
    }
  },
  computed: {
    saveButtonText() {
      if (this.saveState === 'saving') {
        return $t('Saving...')
      }
      if (this.saveState === 'saved') {
        return $t('Saved')
      }
      return $t('Save')
    },
    vatTerminology() {
      return vatTerminology
    },
    persistedFiles() {
      return this.expense.hasOwnProperty('files') && this.expense.files.length
        ? this.expense.files.map((file) => ({
            localId: file.localId,
            name: file.name,
            icon: file.icon,
            link: laravelNamedRoute(namedRoutes.expenses.files.get, {
              expense: this.expense.localId,
              file: file?.storageName,
            }),
          }))
        : []
    },
    persistedFileIds() {
      return this.persistedFiles.map((file) => file.localId)
    },
    selectedFileIds() {
      return this.selectedFiles.map((file) => file.localId)
    },
    newlySelectedFileIds() {
      return this.selectedFileIds.filter(
        (selectedFileId) => !this.persistedFileIds.includes(selectedFileId),
      )
    },
    useExistingExpense() {
      return Object.entries(this.expense).length > 0
    },
    amountInCents() {
      return Number(this.amount) * 100
    },
    isDisabled() {
      return (
        this.viewOnly ||
        !this.expenseState.vendor ||
        !this.amountInCents ||
        (this.filesRequired && this.selectedFiles.length < 1) ||
        this.fileComponentIsWorking
      )
    },
    modalId() {
      return this.useExistingExpense
        ? `expense-update-${this.expenseState.localId || ''}`
        : `expense-create-for-report-${this.expenseReportId}`
    },
    modalTitle() {
      if (this.viewOnly) {
        return $t('Expense')
      }
      return $t(this.useExistingExpense ? 'Update expense' : 'Create expense')
    },
  },
  created() {
    if (Object.entries(this.expense).length > 0) {
      this.expenseState = { ...this.expense }
      this.selectedFiles = this.persistedFiles
    }
  },
  methods: {
    handleUpdatedBusyStatus(status) {
      this.fileComponentIsWorking = status
    },
    onCancel(deleteFiles) {
      this.$emit('closed')
      deleteFiles(this.newlySelectedFileIds)
      this.resetFileState()
    },
    reset() {
      this.expenseState = this.getInitialExpenseState()
      this.amount = 0
      this.selectedFiles = []
    },
    saveExpense() {
      if (Object.entries(this.expense).length > 0) {
        this.updateExpense()
      } else {
        this.createExpense()
      }
    },
    updateExpense() {
      this.saveState = 'saving'
      this.$apollo
        .mutate({
          fetchPolicy: 'no-cache',
          mutation: gql`
            mutation (
              $expenseId: Int!
              $vendor: String!
              $description: String
              $amount: Float!
              $currency: String!
              $expenseAt: DateTime
              $files: [ID]
            ) {
              updateExpense(
                input: {
                  id: $expenseId
                  vendor: $vendor
                  description: $description
                  amount: $amount
                  currency: $currency
                  expenseAt: $expenseAt
                  files: { sync: $files }
                }
              ) {
                globalId
                localId
                vendor
                description
                amount
                currency
                expenseAt
                files {
                  globalId
                  localId
                  name
                  icon {
                    icon
                    color
                    thumbnail
                  }
                }
              }
            }
          `,
          variables: {
            expenseId: this.expenseState.localId,
            vendor: this.expenseState.vendor,
            description: this.expenseState.description,
            expenseAt: moment(this.expenseState.expenseAt, this.$options.dateFormat).format(
              'YYYY-MM-DD HH:mm:ss',
            ),
            amount: this.amountInCents,
            currency: this.expenseState.currency,
            files: this.selectedFiles.map((file) => file.localId),
          },
        })
        .then((data) => {
          this.saveState = 'saved'
          const updatedExpense = data?.data?.updateExpense
          if (updatedExpense) {
            this.$store.commit(EXPENSE_REPORT_MUTATIONS.UPDATE_LINE_ITEM, {
              reportId: this.expenseReportId,
              lineItem: updatedExpense,
            })

            this.$emit('expense-updated', updatedExpense)
          }
          this.$emit('closed')
        })
        .catch(() => {
          this.saveState = 'failed'
        })
        .finally(() => {
          setTimeout(() => {
            this.saveState = 'fresh'
          }, 1000)
        })
    },
    createExpense() {
      this.saveState = 'saving'
      this.$apollo
        .mutate({
          fetchPolicy: 'no-cache',
          mutation: gql`
            mutation (
              $expenseReportId: ID!
              $vendor: String!
              $description: String
              $amount: Float!
              $currency: String!
              $expenseAt: DateTime
              $files: [ID]
            ) {
              createExpense(
                input: {
                  expenseReport: { connect: $expenseReportId }
                  vendor: $vendor
                  description: $description
                  amount: $amount
                  currency: $currency
                  expenseAt: $expenseAt
                  files: { sync: $files }
                }
              ) {
                globalId
                localId
                vendor
                description
                amount
                currency
                expenseAt
                files {
                  globalId
                  localId
                  name
                  icon {
                    icon
                    color
                    thumbnail
                  }
                }
              }
            }
          `,
          variables: {
            expenseReportId: this.expenseReportId,
            vendor: this.expenseState.vendor,
            description: this.expenseState.description,
            expenseAt: moment(this.expenseState.expenseAt, this.$options.dateFormat).format(
              'YYYY-MM-DD HH:mm:ss',
            ),
            amount: this.amountInCents,
            currency: this.expenseState.currency || this.expenseReportCurrency,
            files: this.selectedFiles.map((file) => file.localId),
          },
        })
        .then((data) => {
          this.saveState = 'saved'
          const createdExpense = data?.data?.createExpense
          if (createdExpense) {
            this.$store.commit(EXPENSE_REPORT_MUTATIONS.ADD_LINE_ITEM, {
              reportId: this.expenseReportId,
              lineItem: createdExpense,
            })

            this.$emit('expense-created', createdExpense)
          }
          this.$emit('closed')
          this.reset()
        })
        .catch(() => {
          this.saveState = 'failed'
        })
        .finally(() => {
          setTimeout(() => {
            this.saveState = 'fresh'
          }, 1000)
        })
    },
    getInitialExpenseState() {
      return {
        expenseAt: moment().format(this.$options.dateFormat),
      }
    },
    getInitialAmount() {
      const amount = this.expense.amount ?? 0
      const units = Math.floor(amount / 100)
      const cents = amount % 100
      return `${units}.${cents}`
    },
    resetFileState() {
      this.selectedFiles = this.selectedFiles.filter(
        (selectedFile) => !this.newlySelectedFileIds.includes(selectedFile.localId),
      )
    },
  },
}
</script>
